import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import {
    BusinessInput,
    ServingContainerInput,
} from '@barboxx/admin-components';
import {
    CREATE_BUSINESS_SERVING_CONTAINER_SCHEMA,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessServingContainer } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateServingContainerDialog = ({
    onClose,
    onSuccess,
    open,
}: Props) => {
    const notification = useNotifications();
    const { businessId } = useBusinessSelector();

    const formContext = useForm<BusinessServingContainer.CreateRequest>({
        resolver: yupResolver(CREATE_BUSINESS_SERVING_CONTAINER_SCHEMA),
        defaultValues: {
            businessId,
            isActive: true,
        },
    });

    const mutateCreateServingContainer = useMutation({
        mutationKey: ['servingContainer'],
        mutationFn: (data: BusinessServingContainer.CreateRequest) =>
            Api.BusinessSpecific.ServingContainer.postServingContainer(data),
    });

    const onSubmit = async (values: BusinessServingContainer.CreateRequest) => {
        return mutateCreateServingContainer
            .mutateAsync(values)
            .then(({ data }) => {
                notification.show(`Created Service Container Assignment!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                onSuccess();
            })
            .catch(e => {
                notification.show('Error creating serving container!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancelCreate}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Serving Container
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <BusinessInput />
                    <ServingContainerInput />
                    <TextFieldElement
                        label="Display Order"
                        name="displayOrder"
                        type="number"
                        fullWidth
                        slotProps={{
                            htmlInput: {
                                max: 1000,
                                min: 0,
                                step: 1,
                            },
                        }}
                    />
                    <CheckboxElement label="Active" name="isActive" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
